/**
 * B-GTCC-UI1 — Cockpit SVG chart primitives. Hand-built, no charting
 * library, 1:1 against the inline `<svg>` markup used throughout
 * GTCC_REFERENCE_2026-09-01.html (sparklines, threshold/forecast line
 * charts, bar charts, the alert-severity donut). Styling is entirely via
 * the `spark`/`donut-wrap` classes from `cockpit-ui.css` plus
 * `var(--<cockpit-token>)` fills/strokes — no inline hex colors.
 */

export type ChartTone = "ok" | "warn" | "crit" | "info" | "accent";

/** `svg.spark` 72x20 polyline — compact inline trend indicator. */
export function Sparkline({
  points,
  tone = "accent",
  dashed = false,
}: {
  readonly points: string;
  readonly tone?: ChartTone;
  readonly dashed?: boolean;
}) {
  return (
    <svg className="spark" width={72} height={20} viewBox="0 0 72 20" preserveAspectRatio="none">
      <polyline
        points={points}
        fill="none"
        stroke={`var(--${tone})`}
        strokeWidth={1.5}
        strokeDasharray={dashed ? "3 2" : undefined}
      />
    </svg>
  );
}

export interface LineChartSeries {
  readonly points: string;
  readonly tone: ChartTone;
  readonly dashed?: boolean;
}

export interface LineChartMarker {
  readonly cx: number;
  readonly cy: number;
  readonly tone: ChartTone;
}

export interface LineChartAnnotation {
  readonly x: number;
  readonly y: number;
  readonly text: string;
  readonly tone?: ChartTone;
}

/** `svg.spark` 300x{height} — the full-width line chart with optional threshold/forecast. */
export function LineChart({
  height = 60,
  baselineY,
  thresholdY,
  series,
  markers = [],
  annotations = [],
}: {
  readonly height?: 60 | 70 | 90;
  readonly baselineY: number;
  readonly thresholdY?: number;
  readonly series: readonly LineChartSeries[];
  readonly markers?: readonly LineChartMarker[];
  readonly annotations?: readonly LineChartAnnotation[];
}) {
  return (
    <svg className="spark" viewBox={`0 0 300 ${height}`} preserveAspectRatio="none">
      <line x1="0" y1={baselineY} x2="300" y2={baselineY} stroke="var(--line)" />
      {thresholdY !== undefined ? (
        <line
          x1="0"
          y1={thresholdY}
          x2="300"
          y2={thresholdY}
          stroke="var(--warn)"
          strokeDasharray="2 2"
        />
      ) : null}
      {series.map((s, i) => (
        <polyline
          key={i}
          points={s.points}
          fill="none"
          stroke={`var(--${s.tone})`}
          strokeWidth={1.5}
          strokeDasharray={s.dashed ? "4 3" : undefined}
        />
      ))}
      {markers.map((m, i) => (
        <circle key={i} cx={m.cx} cy={m.cy} r={2.5} fill={`var(--${m.tone})`} />
      ))}
      {annotations.map((a, i) => (
        <text
          key={i}
          x={a.x}
          y={a.y}
          fontSize={7}
          fill={a.tone ? `var(--${a.tone})` : "var(--tx3)"}
        >
          {a.text}
        </text>
      ))}
    </svg>
  );
}

export interface BarChartBar {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** `svg.spark` 300x{height} rects — the Historical Trends bar chart. */
export function BarChart({
  height = 60,
  baselineY,
  bars,
  tone = "accent",
}: {
  readonly height?: number;
  readonly baselineY: number;
  readonly bars: readonly BarChartBar[];
  readonly tone?: ChartTone;
}) {
  return (
    <svg className="spark" viewBox={`0 0 300 ${height}`} preserveAspectRatio="none">
      <line x1="0" y1={baselineY} x2="300" y2={baselineY} stroke="var(--line)" />
      <g fill={`var(--${tone})`}>
        {bars.map((b, i) => (
          <rect key={i} x={b.x} y={b.y} width={b.width} height={b.height} />
        ))}
      </g>
    </svg>
  );
}

export interface DonutSegment {
  readonly tone: ChartTone;
  readonly value: number;
}

/**
 * r=15.9 stroke-dasharray ring donut. Segment offsets are computed with the
 * reference's own `25 - cumulativeValueBefore` positioning formula (verified
 * against the Alarm-Severity donut: crit 14@25, warn 29@11, info 36@-18,
 * accent 21@-54 — each matches `25 - runningTotal`).
 */
export function Donut({
  segments,
  centerValue,
  centerLabel,
  size = 92,
}: {
  readonly segments: readonly DonutSegment[];
  readonly centerValue: string;
  readonly centerLabel: string;
  readonly size?: number;
}) {
  let cumulative = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 42 42">
      <circle cx={21} cy={21} r={15.9} fill="none" stroke="var(--panel2)" strokeWidth={6} />
      {segments.map((s, i) => {
        const offset = 25 - cumulative;
        cumulative += s.value;
        return (
          <circle
            key={i}
            cx={21}
            cy={21}
            r={15.9}
            fill="none"
            stroke={`var(--${s.tone})`}
            strokeWidth={6}
            strokeDasharray={`${s.value} ${100 - s.value}`}
            strokeDashoffset={offset}
          />
        );
      })}
      <text
        x={21}
        y={20}
        textAnchor="middle"
        fontSize={9}
        fontWeight={700}
        fill="var(--tx)"
        fontFamily="Sora"
      >
        {centerValue}
      </text>
      <text x={21} y={28} textAnchor="middle" fontSize={4.5} fill="var(--tx3)">
        {centerLabel}
      </text>
    </svg>
  );
}
